/**
 * Hook to fetch contract data from Blockscout Explorer API.
 * Tries Explorer first; caller falls back to RPC when fromExplorer is false or error.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchInvoiceContractTransactions,
  clearExplorerCache,
} from '@/services/explorer-api';
import {
  parseExplorerTransactions,
  type ExplorerStats,
} from '@/utils/event-parser';

export interface UseExplorerDataResult {
  stats: ExplorerStats | null;
  isLoading: boolean;
  error: string | null;
  fromExplorer: boolean;
  refresh: () => Promise<void>;
}

const REFETCH_INTERVAL_MS = 30_000;

export function useExplorerData(): UseExplorerDataResult {
  const [stats, setStats] = useState<ExplorerStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromExplorer, setFromExplorer] = useState(false);

  const fetch = useCallback(async (forceFresh = false) => {
    setIsLoading(true);
    setError(null);
    setFromExplorer(false);
    const RETRY_DELAY_MS = 2_000;
    try {
      console.log('[useExplorerData] 🔍 Fetching PUBLIC data from Explorer API (same for all users), forceFresh:', forceFresh);
      let res = await fetchInvoiceContractTransactions(forceFresh);
      if (!res) {
        console.warn('[useExplorerData] First fetch failed, retrying with fresh data...');
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        res = await fetchInvoiceContractTransactions(true); // Force fresh on retry
      }
      if (!res) {
        console.error('[useExplorerData] ❌ Explorer API returned no data after retries');
        setError('Explorer API returned no data');
        setStats(null);
        return;
      }
      const parsed = parseExplorerTransactions(res);
      console.log('[useExplorerData] ✅ Successfully parsed PUBLIC Explorer data:', {
        totalInvoices: parsed.totalInvoices,
        paidCount: parsed.paidCount,
        pendingCount: parsed.pendingCount,
        cancelledCount: parsed.cancelledCount,
        uniqueCreators: parsed.uniqueCreators.length,
        totalTransactions: res.items?.length || res.result?.length || 0,
        note: 'This data is public and visible to everyone on the blockchain',
      });
      setStats(parsed);
      setFromExplorer(true);
      if (res.items?.length === 0 && parsed.totalInvoices === 0) {
        setFromExplorer(true);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error('[useExplorerData] ❌ Error fetching from Explorer:', msg);
      setError(msg);
      setStats(null);
      setFromExplorer(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always fetch fresh data on mount
    fetch(true);
    const interval = setInterval(() => fetch(false), REFETCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetch]);

  const refresh = useCallback(async () => {
    clearExplorerCache();
    await fetch(true); // Force fresh fetch on manual refresh
  }, [fetch]);

  return { stats, isLoading, error, fromExplorer, refresh };
}
